import React, { useState, useRef, useMemo, useCallback, useLayoutEffect, useEffect, } from "react";
import { Button, Dialog, Classes, FormGroup, HTMLSelect, Spinner, Popover, PopoverInteractionKind, Position, Tooltip, Icon } from "@blueprintjs/core";
import LoadingIndicator from '../shared/LoadingIndicator';
import "./NewOrganisationRelationShip.css";
import ConfirmationDialog from "../../views/ConfirmationDialog";
import Emoji from "../shared/Emoji";
import axios from "axios";
import Cookies from "js-cookie";
import ToastStore from "../../stores/ToastStore";
import { FilledButton } from "../buttons-20250224T111359Z-001/buttons";

function flatten(node, parentPath = null, out = []) {
    if (!node) return out;
    const path = parentPath ? `${parentPath}-${node.id}` : node.id;
    out.push({ id: node.id, parentPath, path, node });
    (node.children || []).forEach((child) => flatten(child, path, out));
    return out;
}

function normalizeTreeData(data) {
    if (!data) return null;
    if (Array.isArray(data)) {
        if (data.length === 0) return null;
        if (data.length === 1) return data[0];
        return { id: "__multi_root__", name: "", email: "", children: data, _isMultiRoot: true };
    }
    return data;
}

function flattenTreeData(tree) {
    if (!tree) return [];
    if (tree._isMultiRoot) {
        return (tree.children || []).flatMap((child) => flatten(child, null, []));
    }
    return flatten(tree);
}

const API_BASE = () => `${Cookies.get("apiEnv")}/organization_structures`;
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.1;

const OrgCard = React.forwardRef(function OrgCard(
    { node, isRoot, hasChildren, childCount, isExpanded, onCountClick, onAdd, onRemove, isLoadingSubordinates, isAdmin },
    ref
) {
    return (
        <div
            ref={ref}
            className={`org-card${isRoot ? " org-card--root" : ""}`}
        >
            {isAdmin && !isRoot && (
                <div className="org-card-actions">
                    <button
                        type="button"
                        className="org-action-btn org-action-btn--remove"
                        onClick={onRemove}
                        data-tooltip="Remove employee"
                        aria-label="Remove employee"
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
            )}

            <img className="org-avatar" src={node.image_file_name || "https://i.pravatar.cc/150?img=11"} alt={node.name} />
            <div className="org-content">
                <h4 className="org-name" title={node.name}>{node.name}</h4>
                <span className="org-designation" title={node.email}>{node.email}</span>
                {node.mobile && <span className="org-mobile" title={node.mobile}>{node.mobile}</span>}
            </div>

            <div
                className={`org-bottom-actions${hasChildren ? "" : " org-bottom-actions--single"}`}
            >
                {hasChildren && (
                    <button
                        type="button"
                        className="org-toggle"
                        onClick={onCountClick}
                        aria-label="View subordinates"
                        disabled={isLoadingSubordinates}
                    >
                        {isExpanded ? "-" : childCount}
                    </button>
                )}
                {isAdmin && (
                    <button
                        type="button"
                        className="org-action-btn org-action-btn--add"
                        onClick={onAdd}
                        data-tooltip="Assign employee"
                        aria-label="Assign employee"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
});

function OrgNode({ node, path, isRoot, registerRef, openAddDialog, removeUser, onCountClick, loadingCountUserId, isAdmin, currentUser, onToggle }) {
    const nodePath = path ? `${path}-${node.id}` : node.id;
    const hasChildren = !!node.children && node.children.length > 0;
    const childCount = hasChildren ? node.children.length : (node.children_count || 0);
    const showToggle = hasChildren || childCount > 0;

    const [visibleCount, setVisibleCount] = useState(25);

    const handleLoadMore = () => {
        setVisibleCount(prev => prev + 25);
        if (onToggle) {
            onToggle();
        }
    };

    const [isExpanded, setIsExpanded] = useState(() => {
        if (node._isExpanded !== undefined) return node._isExpanded;
        // Default to expanded if we actually have children data
        return !!node.children && node.children.length > 0;
    });

    useEffect(() => {
        if (node._isExpanded !== undefined) {
            setIsExpanded(node._isExpanded);
        }
    }, [node._isExpanded]);

    const handleToggleClick = () => {
        if (!isExpanded) {
            onCountClick(node);
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
        if (onToggle) {
            onToggle();
        }
    };

    return (
        <div className="org-node">
            <OrgCard
                ref={registerRef(nodePath)}
                node={node}
                isRoot={isRoot}
                hasChildren={showToggle}
                childCount={childCount}
                isExpanded={isExpanded}
                onCountClick={handleToggleClick}
                onAdd={() => openAddDialog(node.id, isRoot)}
                onRemove={() => removeUser(nodePath)}
                isLoadingSubordinates={loadingCountUserId === node.id}
                isAdmin={isAdmin}
            />

            {showToggle && isExpanded && hasChildren && (
                <div className="children">
                    {node.children.slice(0, visibleCount).map((child) => (
                        <div className="org-child" key={`${nodePath}-${child.id}`}>
                            <OrgNode
                                node={child}
                                path={nodePath}
                                isRoot={false}
                                registerRef={registerRef}
                                openAddDialog={openAddDialog}
                                removeUser={removeUser}
                                onCountClick={onCountClick}
                                loadingCountUserId={loadingCountUserId}
                                isAdmin={isAdmin}
                                currentUser={currentUser}
                                onToggle={onToggle}
                            />
                        </div>
                    ))}
                    {node.children.length > visibleCount && (
                        <div className="org-child" style={{ justifyContent: 'center' }}>
                            <Button
                                onClick={handleLoadMore}
                                text={`+${node.children.length - visibleCount} More`}
                                style={{ marginTop: '20px', borderRadius: '20px', padding: '5px 15px', background: '#fff', border: '1px solid #d7dbe3', color: '#374151', fontWeight: 600, boxShadow: '0 2px 6px rgba(15, 23, 42, .08)' }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const NewOrganisationRelationShip = ({ currentUser }) => {
    const [orgTree, setOrgTree] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [viewContext, setViewContext] = useState({ type: "department" });
    const [loadingCountUserId, setLoadingCountUserId] = useState(null);
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
    const [isRemoveSubmitting, setIsRemoveSubmitting] = useState(false);
    const [lines, setLines] = useState([]);
    const [removeTarget, setRemoveTarget] = useState(null);

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isAddDialogRoot, setIsAddDialogRoot] = useState(false);
    const [isAddSubmitting, setIsAddSubmitting] = useState(false);
    const [selectedManagerId, setSelectedManagerId] = useState(null);
    const [selectedChildUserId, setSelectedChildUserId] = useState("");
    const [relationshipType, setRelationshipType] = useState("add_to_child");
    const [allUsers, setAllUsers] = useState([]);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [forestVisibleCount, setForestVisibleCount] = useState(25);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef(null);
    const wrapperRef = useRef(null);
    const panelRef = useRef(null);
    const nodeRefs = useRef(new Map());

    const pairs = useMemo(() => flattenTreeData(orgTree), [orgTree]);

    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;

        const handleWheel = (e) => {
            if (e.ctrlKey) {
                e.preventDefault();

                if (panel.contains(e.target)) {
                    e.stopPropagation();
                    setZoomLevel((z) => {
                        const zoomChange = -(e.deltaY * 0.002);
                        return Math.min(Math.max(z + zoomChange, 0.4), 2);
                    });
                }
            }
        };

        document.addEventListener("wheel", handleWheel, { passive: false });
        return () => document.removeEventListener("wheel", handleWheel);
    }, []);

    const zoomRAFRef = useRef(null);

    const registerRef = useCallback(
        (id) => (el) => {
            if (el) nodeRefs.current.set(id, el);
            else nodeRefs.current.delete(id);
        },
        []
    );

    const applyTree = useCallback((data) => {
        nodeRefs.current.clear();
        setOrgTree(normalizeTreeData(data));
        setLines([]);
        setForestVisibleCount(25);
    }, []);

    const loadDepartmentHierarchy = useCallback(async (userId, background = false) => {
        if (!userId) return;
        if (!background) {
            setIsLoading(true);
            setLines([]);
        }
        try {
            const response = await axios.get(`${API_BASE()}/hierarchy`, { params: { user_id: userId } });
            applyTree(response?.data);
            setViewContext({ type: "department", userId });
        } catch (error) {
            ToastStore.showToast("Failed to load hierarchy", "danger");
        } finally {
            if (!background) {
                setIsLoading(false);
            }
        }
    }, [applyTree]);

    const loadUserSubordinates = useCallback(async (node, background = false) => {
        if (!node?.id) return;
        if (!background) {
            setLoadingCountUserId(node.id);
        }
        try {
            const response = await axios.get(`${API_BASE()}/user_hierarchy`, { params: { user_id: node.id } });
            const children = Array.isArray(response?.data) ? response?.data : [];

            setOrgTree(prevTree => {
                if (!prevTree) return prevTree;

                const updateNodeInChildren = (current, targetId, newChildren) => {
                    if (current.id === targetId) {
                        return { ...current, children: newChildren, _isExpanded: true };
                    }
                    if (current.children) {
                        return {
                            ...current,
                            children: current.children.map(child => updateNodeInChildren(child, targetId, newChildren))
                        };
                    }
                    return current;
                };

                if (prevTree._isMultiRoot) {
                    return {
                        ...prevTree,
                        children: prevTree.children.map(child => updateNodeInChildren(child, node.id, children))
                    };
                }
                return updateNodeInChildren(prevTree, node.id, children);
            });
        } catch (error) {
            ToastStore.showToast("Failed to load subordinates", "danger");
        } finally {
            if (!background) {
                setLoadingCountUserId(null);
            }
        }
    }, []);

    const loadFullHierarchy = useCallback(async (background = false) => {
        if (!background) {
            setIsLoading(true);
            setLines([]);
        }
        try {
            const response = await axios.get(`${API_BASE()}/hierarchy`);
            const data = response?.data;
            applyTree(data);
            setViewContext({ type: "full" });

            if (data && !Array.isArray(data) && (!data.children || data.children.length === 0) && data.children_count > 0) {
                loadUserSubordinates(data, true);
            }
        } catch (error) {
            ToastStore.showToast("Failed to load hierarchy", "danger");
        } finally {
            if (!background) {
                setIsLoading(false);
            }
        }
    }, [applyTree, loadUserSubordinates]);

    const refreshCurrentView = useCallback(async (background = true) => {
        if (viewContext.type === "full") {
            await loadFullHierarchy(background);
        } else if (viewContext.type === "Manages" && viewContext.node) {
            await loadUserSubordinates(viewContext.node, background);
        } else {
            await loadDepartmentHierarchy(viewContext.userId || currentUser?.id, background);
        }
    }, [viewContext, loadFullHierarchy, loadUserSubordinates, loadDepartmentHierarchy, currentUser?.id]);

    const handleCountClick = useCallback((node) => {
        loadUserSubordinates(node);
    }, [loadUserSubordinates]);

    const recompute = useCallback(() => {
        try {
            const wrapper = wrapperRef.current;
            if (!wrapper) return;

            const validPaths = new Set(pairs.map((p) => p.path));
            nodeRefs.current.forEach((_, path) => {
                if (!validPaths.has(path)) nodeRefs.current.delete(path);
            });

            const wrapperRect = wrapper.getBoundingClientRect();
            const byParent = new Map();
            pairs.forEach((p) => {
                if (p.parentPath == null) return;
                if (!byParent.has(p.parentPath)) byParent.set(p.parentPath, []);
                byParent.get(p.parentPath).push(p);
            });

            const newLines = [];

            byParent.forEach((children, parentPath) => {
                const parentEl = nodeRefs.current.get(parentPath);
                if (!parentEl) return;

                const visible = children
                    .map((c) => ({ ...c, el: nodeRefs.current.get(c.path) }))
                    .filter((c) => c.el && c.el.offsetWidth > 0 && c.el.offsetHeight > 0);
                if (visible.length === 0) return;

                const unscale = (val) => val / zoomLevel;

                const pRect = parentEl.getBoundingClientRect();
                const parentX = unscale(pRect.left + pRect.width / 2 - wrapperRect.left);
                const parentY = unscale(pRect.bottom - wrapperRect.top);

                const childPoints = visible.map((c) => {
                    const r = c.el.getBoundingClientRect();
                    return {
                        id: c.id,
                        path: c.path,
                        x: unscale(r.left + r.width / 2 - wrapperRect.left),
                        y: unscale(r.top - wrapperRect.top),
                    };
                });

                const midY = parentY + (childPoints[0].y - parentY) / 2;
                const xs = [parentX, ...childPoints.map((c) => c.x)];
                const busMin = Math.min(...xs);
                const busMax = Math.max(...xs);
                const LINE_COLOR = "#d6dae3";

                newLines.push({ key: `parent-drop-${parentPath}`, x1: parentX, y1: parentY, x2: parentX, y2: midY, color: LINE_COLOR });

                if (busMax - busMin > 0.5) {
                    newLines.push({ key: `bus-${parentPath}`, x1: busMin, y1: midY, x2: busMax, y2: midY, color: LINE_COLOR });
                }

                childPoints.forEach((c) => {
                    newLines.push({ key: `child-drop-${c.path}`, x1: c.x, y1: midY, x2: c.x, y2: c.y, color: LINE_COLOR });
                });
            });

            setLines(newLines);
        } catch (e) {
            console.error("Error computing organization chart lines:", e);
        }
    }, [pairs, zoomLevel]);

    const recomputeAfterPaint = useCallback(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                recompute();
            });
        });
    }, [recompute]);

    const animateRecompute = useCallback((duration = 220) => {
        if (zoomRAFRef.current) cancelAnimationFrame(zoomRAFRef.current);

        const start = performance.now();
        const step = (now) => {
            recompute();
            if (now - start < duration) {
                zoomRAFRef.current = requestAnimationFrame(step);
            } else {
                zoomRAFRef.current = null;
            }
        };
        zoomRAFRef.current = requestAnimationFrame(step);
    }, [recompute]);

    useEffect(() => {
        animateRecompute();
        return () => {
            if (zoomRAFRef.current) cancelAnimationFrame(zoomRAFRef.current);
        };
    }, [zoomLevel, animateRecompute]);

    useEffect(() => {
        return () => {
            if (zoomRAFRef.current) cancelAnimationFrame(zoomRAFRef.current);
        };
    }, []);

    useEffect(() => {
        if (currentUser?.id) {
            loadDepartmentHierarchy(currentUser.id);
        }
        axios.get(`${Cookies.get("apiEnv")}/users?per_page=1000`)
            .then(res => setAllUsers(res.data || []))
            .catch(() => setAllUsers([]));
    }, [currentUser?.id, loadDepartmentHierarchy]);

    useEffect(() => {
        if (!isLoading && orgTree) {
            recomputeAfterPaint();
            const timer = setTimeout(() => {
                recomputeAfterPaint();
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [isLoading, orgTree, recomputeAfterPaint]);

    const openAddDialog = useCallback((managerId, isRootNode = false) => {
        setSelectedManagerId(managerId);
        setSelectedChildUserId("");
        setRelationshipType("add_to_child");
        setIsAddDialogRoot(isRootNode);
        setIsAddDialogOpen(true);
    }, []);

    const closeAddDialog = useCallback(() => setIsAddDialogOpen(false), []);

    const handleAddEmployee = useCallback(async () => {
        if (!selectedChildUserId || !selectedManagerId) return;
        setIsAddSubmitting(true);

        try {
            const payload = {
                relationship_type: relationshipType,
                parent_user_id: relationshipType === "add_to_child" ? selectedManagerId : selectedChildUserId,
                child_user_id: relationshipType === "add_to_child" ? selectedChildUserId : selectedManagerId
            };

            const response = await axios.post(`${Cookies.get("apiEnv")}/organization_structures/add_member`, payload);

            if (response.data && response.data.status === 200) {
                ToastStore.showToast(response?.data?.message || "Member added to organization", "success");
                await refreshCurrentView(true);

                setOrgTree(prevTree => {
                    if (!prevTree) return prevTree;
                    const expandNode = (current, targetIds) => {
                        if (targetIds.includes(current.id)) {
                            return { ...current, _isExpanded: true };
                        }
                        if (current.children) {
                            return { ...current, children: current.children.map(child => expandNode(child, targetIds)) };
                        }
                        return current;
                    };
                    const targetIds = [selectedManagerId, selectedChildUserId];
                    if (prevTree._isMultiRoot) {
                        return { ...prevTree, children: prevTree.children.map(child => expandNode(child, targetIds)) };
                    }
                    return expandNode(prevTree, targetIds);
                });

                recomputeAfterPaint();
                setIsAddDialogOpen(false);
            } else {
                ToastStore.showToast(response?.data?.error || "Failed to add member", "danger");
            }
        } catch (error) {
            ToastStore.showToast(error.response?.data?.error || "Failed to add member", "danger");
        } finally {
            setIsAddSubmitting(false);
        }
    }, [selectedChildUserId, selectedManagerId, relationshipType, refreshCurrentView, recomputeAfterPaint]);

    const removeUser = useCallback(
        (path) => {
            if (!orgTree || path === orgTree.id) return;
            const pair = pairs.find(p => p.path === path);
            if (!pair) return;
            setRemoveTarget(pair);
            setIsRemoveDialogOpen(true);
        },
        [orgTree, pairs]
    );

    const closeRemoveDialog = useCallback(() => {
        setIsRemoveDialogOpen(false);
        setRemoveTarget(null);
    }, []);

    const confirmRemove = useCallback(async () => {
        if (!removeTarget) return;
        setIsRemoveSubmitting(true);

        const parentPath = removeTarget.parentPath;
        const parentPair = pairs.find(p => p.path === parentPath);
        const parentId = parentPair ? parentPair.id : null;

        try {
            const response = await axios.delete(`${Cookies.get("apiEnv")}/organization_structures/remove_member`, {
                data: {
                    parent_user_id: parentId,
                    child_user_id: removeTarget.id
                }
            });

            if (response.data && response.data.status === 200) {
                ToastStore.showToast(response.data.message || "Member removed from organization", "success");
                await refreshCurrentView(true);
                recomputeAfterPaint();
                closeRemoveDialog();
            } else {
                ToastStore.showToast(response.data?.error || "Failed to remove member", "danger");
            }
        } catch (error) {
            ToastStore.showToast(error.response?.data?.error || "Failed to remove member", "danger");
        } finally {
            setIsRemoveSubmitting(false);
        }
    }, [removeTarget, pairs, refreshCurrentView, closeRemoveDialog, recomputeAfterPaint]);

    useLayoutEffect(() => {
        recompute();
    }, [recompute, orgTree]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return undefined;

        const ro = new ResizeObserver(() => recompute());
        ro.observe(wrapper);
        window.addEventListener("resize", recompute);

        const imgs = wrapper.querySelectorAll("img");
        imgs.forEach((img) => {
            img.addEventListener("load", recompute);
            img.addEventListener("error", recompute);
        });

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", recompute);
            imgs.forEach((img) => {
                img.removeEventListener("load", recompute);
                img.removeEventListener("error", recompute);
            });
        };
    }, [recompute]);

    if (isLoading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <LoadingIndicator />
            </div>
        );
    }

    const handleForestLoadMore = () => {
        setForestVisibleCount(prev => prev + 25);
        animateRecompute();
    };

    const renderTreeNodes = () => {
        const isAdmin = (currentUser?.isAccountOwner || currentUser?.is_account_owner) && viewContext.type === "full";
        if (orgTree._isMultiRoot) {
            const visibleChildren = orgTree.children.slice(0, forestVisibleCount);
            const hasMore = orgTree.children.length > forestVisibleCount;

            return (
                <div className="org-forest-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="org-forest" style={{ width: '100%' }}>
                        {visibleChildren?.map((child) => (
                            <OrgNode
                                key={child.id}
                                node={child}
                                isRoot
                                registerRef={registerRef}
                                openAddDialog={openAddDialog}
                                removeUser={removeUser}
                                onCountClick={handleCountClick}
                                loadingCountUserId={loadingCountUserId}
                                isAdmin={isAdmin}
                                currentUser={currentUser}
                                onToggle={animateRecompute}
                            />
                        ))}
                    </div>
                    {hasMore && (
                        <div style={{ marginTop: '40px', marginBottom: '20px' }}>
                            <Button
                                onClick={handleForestLoadMore}
                                text={`Load More (+${orgTree.children.length - forestVisibleCount})`}
                                style={{ background: '#1870d5', color: '#fff', borderRadius: '20px', padding: '8px 25px', border: 'none', fontWeight: 600, boxShadow: '0 4px 10px rgba(24, 112, 213, 0.3)' }}
                            />
                        </div>
                    )}
                </div>
            );
        }

        return (
            <OrgNode
                node={orgTree}
                isRoot
                registerRef={registerRef}
                openAddDialog={openAddDialog}
                removeUser={removeUser}
                onCountClick={handleCountClick}
                loadingCountUserId={loadingCountUserId}
                isAdmin={isAdmin}
                currentUser={currentUser}
                onToggle={animateRecompute}
            />
        );
    };

    const handleDownloadSample = () => {
        setIsDownloading(true);

        const link = document.createElement("a");
        link.href = "/sample_import_organizations.xlsx";
        link.download = "sample_import_organizations.xlsx";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
            setIsDownloading(false);
        }, 800);
    };

    const handleUploadClick = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }
        fileInputRef.current?.click();
    };

    const buildUploadToastMessage = (data) => {
        const message = data?.message || "File uploaded successfully";
        const totalCreated = data?.total_created ?? 0;
        const totalErrors = data?.total_errors ?? 0;
        const totalSkipped = data?.total_skipped ?? 0;
        return `${message}\nCreated: ${totalCreated}\nErrors: ${totalErrors}\nSkipped: ${totalSkipped}`;
    };
    const handleFileChange = async (e) => {
        const input = e.target;
        const file = input.files?.[0];
        if (!file) return;
        const allowedExtensions = ["xls", "xlsx", "xlsm"];
        const fileExt = file.name.split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExt)) {
            ToastStore?.showToast?.("Invalid file format", "danger");
            input.value = "";
            return;
        }
        const baseUrl = Cookies.get("apiEnv");
        if (!baseUrl) {
            ToastStore?.showToast?.("API base URL not found", "danger");
            input.value = "";
            return;
        }
        const formData = new FormData();
        formData.append("file", file);
        try {
            setIsUploading(true);
            setIsLoading(true);
            const response = await axios.post(
                `${baseUrl}/organization_structures/organization_structures_import`,
                formData
            );
            const data = response?.data || {};
            const toastMessage = buildUploadToastMessage(data);
            const hasErrors = Number(data.total_errors ?? 0) > 0;
            const hasCreated = Number(data.total_created ?? 0) > 0;
            const toastIntent = hasErrors
                ? "warning"
                : hasCreated
                    ? "success"
                    : "danger";
            ToastStore?.showToast?.(toastMessage, toastIntent);
            if (hasCreated) {
                await refreshCurrentView(true);
            }
        } catch (error) {
            console.error("Organization import failed:", error);
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "File upload failed";
            ToastStore?.showToast?.(message, "danger");
        } finally {
            setIsUploading(false);
            setIsLoading(false);
            input.value = "";
        }
    };

    const openCreateDrawer = () => {
        resetForm();
        setIsDrawerOpen(true);
    };

    return (
        <>
            <div className="organisation-tree" style={{ position: "relative" }}>
                <div className="organisation-tree__topbar">
                    <div className="organisation-tree__topbar-section organisation-tree__topbar-section--left">
                        <div className="organisation-tree__view-switcher">
                            {currentUser && (
                                <Button
                                    minimal
                                    icon="people"
                                    text={I18n.t('js.my_department')}
                                    className={viewContext.type === "department" ? "org-switcher-btn org-switcher-btn--active" : "org-switcher-btn"}
                                    onClick={() => loadDepartmentHierarchy(currentUser?.id)}
                                    style={{ borderRadius: 15 }}
                                />
                            )}
                            <Button
                                minimal
                                icon="diagram-tree"
                                text={I18n.t('js.full_organization')}
                                className={viewContext.type === "full" ? "org-switcher-btn org-switcher-btn--active" : "org-switcher-btn"}
                                onClick={() => loadFullHierarchy()}
                                style={{ borderRadius: 15 }}
                            />
                        </div>
                    </div>
                    <div className="organisation-tree__topbar-section organisation-tree__topbar-section--right">
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Popover
                                position={Position.BOTTOM_RIGHT}
                                interactionKind={PopoverInteractionKind.CLICK}
                                portalContainer={document.body}
                                content={
                                    <div
                                        style={{
                                            borderRadius: "12px",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                            border: "1px solid #e5e7eb",
                                            overflow: "hidden",
                                            background: "#fff",
                                            width: "260px",
                                        }}
                                    >
                                        {/* Download */}
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "12px",
                                                padding: "12px 16px",
                                                alignItems: "center",
                                                cursor: "pointer",
                                            }}
                                            onClick={handleDownloadSample}
                                        >
                                            <div
                                                style={{
                                                    background: "#e0edff",
                                                    color: "#2d72d9",
                                                    borderRadius: "8px",
                                                    padding: "8px",
                                                    display: "flex",
                                                }}
                                            >
                                                <Icon icon="download" />
                                            </div>

                                            <div>
                                                <div style={{ fontWeight: 600 }}>Download Sample</div>
                                                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                                    Download a sample file for the import format
                                                </div>
                                            </div>
                                        </div>

                                        {/* Upload (highlighted like screenshot) */}
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "12px",
                                                padding: "12px 16px",
                                                alignItems: "center",
                                                cursor: "pointer",
                                                background: "#f3f4f6",
                                            }}
                                            onClick={handleUploadClick}
                                        >
                                            <div
                                                style={{
                                                    background: "#dcfce7",
                                                    color: "#16a34a",
                                                    borderRadius: "8px",
                                                    padding: "8px",
                                                    display: "flex",
                                                }}
                                            >
                                                <Icon icon="upload" />
                                            </div>

                                            <div>
                                                <div style={{ fontWeight: 600 }}>Upload File</div>
                                                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                                    Upload a file to import organization data
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }
                            >
                                {/* <Button
                                    style={{
                                        borderRadius: "10px",
                                        color: "#2d72d9",
                                        // backgroundColor: "rgba(45, 114, 217, 0.08)",
                                        border: "1px solid rgba(45, 114, 217, 0.3)",
                                        padding: "9px 18px",
                                        fontWeight: 500,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                    icon="import"
                                    rightIcon="caret-down"
                                    text="Import Organization Employee"
                                /> */}
                            </Popover>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                accept=".xls,.xlsx,.xlsm,.xlsb,.xlt,.xltx,.ods,.csv"
                                onChange={handleFileChange}
                            />

                        </div>
                        <div className="organisation-tree__zoom-controls">
                            <Button
                                minimal
                                icon="zoom-out"
                                className="org-zoom-btn"
                                onClick={() => setZoomLevel((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN))}
                            />
                            <div className="organisation-tree__zoom-value-wrapper">
                                <span className="organisation-tree__zoom-value-label">{Math.round(zoomLevel * 100)}%</span>
                            </div>
                            <Button
                                minimal
                                icon="zoom-in"
                                className="org-zoom-btn"
                                onClick={() => setZoomLevel((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX))}
                            />
                            <Button
                                minimal
                                icon="refresh"
                                className="org-zoom-btn"
                                onClick={() => setZoomLevel(1)}
                                title="Reset Zoom"
                            />
                        </div>
                    </div>
                </div>
                <div className="organisation-tree__panel" ref={panelRef}>
                    {(!orgTree || (Array.isArray(orgTree) && orgTree.length === 0) || orgTree.message || !orgTree.id) ? (
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "60vh", color: "#6b7280" }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "16px", color: "#9ca3af" }}>
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#374151", margin: "0 0 8px 0" }}>No Organization Data</h3>
                            <p style={{ margin: 0 }}>{orgTree?.message || "You are not a part of any team."}</p>
                        </div>
                    ) : (
                        <div className="organisation-tree__canvas" ref={wrapperRef} style={{ transform: `scale(${zoomLevel})`, transformOrigin: "top center", transition: "transform 0.2s ease-out" }}>
                            <svg
                                className="org-connectors"
                                style={{ width: '100%', height: '100%' }}
                                aria-hidden="true"
                            >
                                {lines?.map((l) => (
                                    <line
                                        key={l.key}
                                        x1={l.x1}
                                        y1={l.y1}
                                        x2={l.x2}
                                        y2={l.y2}
                                        stroke={l.color}
                                        strokeWidth={1}
                                        strokeLinecap="round"
                                    />
                                ))}
                            </svg>

                            {renderTreeNodes()}
                        </div>
                    )}
                </div>
            </div>

            <Dialog
                isOpen={isAddDialogOpen}
                title="Assign Reporting Relationship"
                onClose={closeAddDialog}
                className="org-add-dialog"
                portalContainer={document.body}
                canOutsideClickClose={false}

            >
                <div className={`${Classes.DIALOG_BODY} org-dialog-body`}>
                    {isAddDialogRoot && (
                        <FormGroup label="Relationship" className="org-dialog-field org-dialog-field--relationship">
                            <div className="org-dialog-relationship-toggle" role="group" aria-label="Relationship selector">
                                <button
                                    type="button"
                                    className={`org-dialog-relationship-option ${relationshipType === 'add_to_parent' ? 'org-dialog-relationship-option--active' : ''}`}
                                    onClick={() => setRelationshipType('add_to_parent')}
                                >
                                    Reports to
                                </button>
                                <button
                                    type="button"
                                    className={`org-dialog-relationship-option ${relationshipType === 'add_to_child' ? 'org-dialog-relationship-option--active' : ''}`}
                                    onClick={() => setRelationshipType('add_to_child')}
                                >
                                    Manages
                                </button>
                            </div>
                        </FormGroup>
                    )}
                    <FormGroup label="Employee" className="org-dialog-field">
                        <HTMLSelect
                            fill
                            className="org-dialog-select"
                            value={selectedChildUserId}
                            onChange={(e) => setSelectedChildUserId(e.target.value)}
                        >
                            <option value="">Select a user...</option>
                            {allUsers.filter((u) => !pairs.some((p) => p.id === u.id)).map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.full_name} ({u.email})
                                </option>
                            ))}
                        </HTMLSelect>
                    </FormGroup>
                </div>

                <div className={`${Classes.DIALOG_FOOTER} org-dialog-footer`}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
                        <Button
                            style={{
                                background: "#1870d5",
                                color: "#fff",
                                borderRadius: "25px",
                                padding: "10px 25px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "auto",
                                minWidth: "190px",
                            }}
                            className="org-dialog-btn org-dialog-btn--primary"
                            onClick={handleAddEmployee}
                            disabled={!selectedChildUserId || isAddSubmitting}
                        >
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                {isAddSubmitting && <LoadingIndicator size={16} style={{ color: "#fff" }} />}
                                {isAddSubmitting ? "Assigning employee" : "Assign Employee"}
                            </span>
                        </Button>
                    </div>
                </div>
            </Dialog>

            <ConfirmationDialog
                isOpen={isRemoveDialogOpen}
                onClose={() => setIsRemoveDialogOpen(false)}
                onConfirm={confirmRemove}
                title="Remove hierarchy"
                message={`Are you sure you want to remove ${removeTarget?.node?.name} ?`}
                confirmText="Remove"
                isSubmitting={isRemoveSubmitting}
            />
        </>
    );
};

export default NewOrganisationRelationShip;
